import type { EnPracticeMap } from "./en-types";

/** English practice content: litigation, corporate, family, estate, property. */
export const EN_PRACTICE_A: EnPracticeMap = {
  litigation: {
    slug: "litigation",
    name: "Litigation & Dispute Resolution",
    tagline: "Civil, criminal, labour and administrative matters through to enforcement",
    seoTitle: "Litigation Lawyers in Thailand — Civil, Criminal & Labour Courts",
    seoDesc:
      "Thai litigation and enforcement: what documents to bring, how a case actually proceeds, limitation-period pitfalls and answers to common questions. Talk to the team first.",
    answer:
      "A Thai litigation matter means the firm reviews your evidence, checks the limitation period, drafts and files the complaint or defence, appears at hearings on your behalf, and continues into judgment enforcement if the other side does not pay. We advise before any filing so the approach fits your actual facts and documents. Fees are quoted per matter after we review the file.",
    audience: [
      "People who received a court summons and do not know what to do first",
      "Creditors whose demand letters have gone unanswered",
      "Companies in dispute with a supplier, customer or contractor",
      "Employers or employees whose dispute has reached the Labour Court",
      "Foreign parties who need an interpreter and certified translations for a Thai case",
    ],
    scope: [
      "Case assessment: evidence, limitation period and realistic options",
      "Demand letters, settlement negotiation and mediation",
      "Drafting and filing complaints, defences, counterclaims and interim applications",
      "Court appearances and witness examination on your behalf",
      "Appeals to the Court of Appeal and the Supreme Court",
      "Asset investigation, seizure, attachment and auction at the enforcement stage",
    ],
    examples: [
      "Breach of a sale, construction or service agreement",
      "Recovery of loans, guarantees, mortgages and trade debt",
      "Damages claims arising from accidents or property damage",
      "Cheque, fraud and misappropriation matters connected to a business",
      "Unfair dismissal, severance and wage disputes",
      "Enforcing a final judgment against a debtor who will not pay",
    ],
    docs: [
      "Every contract or document showing the legal relationship",
      "Payment records: transfer slips, receipts, invoices",
      "Chat logs, emails and correspondence between the parties",
      "ID card or company affidavit for your own side",
      "Details of the other party: registered address and any known assets",
      "Any summons, complaint or earlier case file you have received",
    ],
    steps: [
      "Send your documents and describe what happened so the team can assess the file",
      "The lawyer sets out the options, risks and scope of work in writing",
      "You sign the power of attorney and appointment, and we assemble the evidence",
      "We negotiate, or file the complaint or defence within the limitation period",
      "We appear at hearings, examine witnesses and report after every hearing",
      "We handle appeal or enforcement steps depending on the outcome",
    ],
    cautions: [
      {
        title: "A missed limitation period cannot be repaired",
        body: "Each type of claim has its own limitation period and its own starting point. Once it has expired the other side can raise it as a defence, so bring your documents in early rather than after further negotiation.",
      },
      {
        title: "Filing in the wrong court costs months",
        body: "Contract, labour, tax, intellectual property, bankruptcy and administrative matters go to different courts, and jurisdiction also depends on domicile and where the events occurred. We confirm this from your documents before filing.",
      },
      {
        title: "Winning is not the same as being paid",
        body: "If the other side does not comply with the judgment, enforcement is a separate stage with its own deadlines, filings and asset investigation. Plan for it from the start when the other side's assets are unclear.",
      },
    ],
    compare: {
      heading: "Demand letter, mediation or court proceedings",
      intro:
        "These three paths are not substitutes for each other. Many matters begin with a demand letter and move on when it produces nothing. The table shows how the workload and the binding effect differ.",
      columns: ["Negotiation / demand letter", "Mediation", "Court proceedings"],
      rows: [
        {
          label: "Binding effect",
          cells: [
            "Binding once a signed settlement record exists",
            "A settlement recorded by the court or agency can be enforced",
            "A final judgment can be enforced",
          ],
        },
        {
          label: "Typical duration",
          cells: [
            "Shortest of the three, depending on the other side's cooperation",
            "Moderate, depending on the mediator's schedule",
            "Longest: several hearings, and possibly appeals",
          ],
        },
        {
          label: "Documents needed",
          cells: [
            "Contract, payment records, contact history",
            "The same, plus identification for everyone attending",
            "A complete evidence bundle plus the witnesses named in the pleadings",
          ],
        },
        {
          label: "Best suited to",
          cells: [
            "A clear debt where the other side still responds",
            "Situations where the commercial relationship should continue",
            "Denied liability, or a limitation period about to expire",
          ],
        },
        {
          label: "Commonly overlooked",
          cells: [
            "Keeping no written record of the demand",
            "Agreeing verbally and never recording it",
            "Filing too late, or in a court without jurisdiction",
          ],
        },
      ],
      note: "Every matter turns on its own facts and documents. The team assesses yours before any work begins.",
    },
    handoff:
      "You can hand the whole matter to the firm: one file, one responsible lawyer, and a report after every hearing. Where the case needs certified translations, an interpreter or notarial certification of foreign documents, those are handled in the same office rather than by separate providers.",
    faqs: [
      {
        question: "I received a court summons. What should I do first?",
        answer:
          "Check the hearing date and the deadline for filing a defence, because missing it can cost you the chance to defend the case. Collect every contract, receipt and message relating to the dispute, then bring them together with the summons to a lawyer well before the hearing date.",
      },
      {
        question: "Why does the limitation period matter so much?",
        answer:
          "The limitation period is the window in which a claim can be pursued in court. Each type of claim has a different period and a different starting event. Once it has run out the other side can rely on it as a defence, so we verify the starting date from your documents before advising on strategy.",
      },
      {
        question: "Which court will hear my case?",
        answer:
          "That depends on the type of dispute and on jurisdiction. Ordinary contract claims, employment disputes and claims against a state agency each go to a different court, and the parties' domicile and the place of the events also matter. Filing in the wrong court wastes time and may mean starting again.",
      },
      {
        question: "Do I have to attend every hearing?",
        answer:
          "No. Many hearings are procedural and the lawyer attends for you. Hearings where you give evidence yourself require you to be present. We tell you in advance who needs to attend each hearing so you can plan work and travel.",
      },
      {
        question: "Can chat messages and emails be used as evidence?",
        answer:
          "They can be submitted as documentary evidence, but keep the full conversation rather than selected lines, and preserve the information showing who sent what and when. Material in a foreign language needs a certified Thai translation before it can be used.",
      },
      {
        question: "If I win, do I receive the money immediately?",
        answer:
          "Not immediately. Once the judgment is final and the other side still does not pay, the matter moves into enforcement: investigating assets, then seizure or attachment. That stage has its own filings, costs and timeframes, and it depends on the debtor actually having assets.",
      },
      {
        question: "My counterparty is overseas. Can I still sue in Thailand?",
        answer:
          "Often yes, but serving documents abroad takes considerably longer than domestic service and usually requires certified translations and legalisation. Because the firm handles translation and certification in-house, those steps can run alongside the case rather than after it.",
      },
      {
        question: "What costs arise besides legal fees?",
        answer:
          "Typically court fees, service fees, copying charges, interpreter or translation costs where foreign-language documents are involved, and further costs if the matter reaches enforcement. We list the items that apply to your matter before work starts.",
      },
    ],
    related: ["corporate", "labour", "property"],
    imageAlt: "Lawyers reviewing a case file and documentary evidence together in a Bangkok law-office meeting room",
  },

  corporate: {
    slug: "corporate",
    name: "Corporate & Commercial",
    tagline: "Company formation, registry changes, contracts and foreign-investment structures",
    seoTitle: "Corporate Lawyers in Thailand — Company Setup & Contracts",
    seoDesc:
      "Company registration, registry amendments, contract drafting and review, foreign shareholding structures and legal due diligence, with documents, steps and common pitfalls.",
    answer:
      "Corporate work covers incorporating a Thai entity, amending directors and objectives in the registry, drafting and reviewing commercial contracts, structuring shareholdings for foreign investors, and legal due diligence for a purchase or sale. The firm reviews registry documents and contractual risk together, because the two usually create each other's problems. Fees are quoted per matter.",
    audience: [
      "Founders incorporating a company and setting up the first document set correctly",
      "Companies changing directors, address, objectives or signing authority",
      "Foreign investors who need business-activity restrictions checked before investing",
      "Anyone about to sign an important agreement who wants it reviewed first",
      "Buyers or sellers of a business who need legal due diligence",
    ],
    scope: [
      "Name reservation, incorporation documents and registry filings",
      "Amendments: directors, address, objectives, capital, signing authority",
      "Drafting and reviewing commercial, supply, service and shareholder agreements",
      "Foreign-investment structuring within the limits of Thai law",
      "Legal due diligence for share or asset transactions",
      "Corporate housekeeping: minutes, shareholder registers, annual meetings",
    ],
    examples: [
      "Setting up a Thai company for a first venture or a regional office",
      "Adding or removing a director and updating signing authority",
      "Reviewing a distribution or manufacturing agreement before signature",
      "Checking whether a planned activity is restricted for foreign shareholders",
      "Preparing a shareholders' agreement between local and foreign partners",
      "Reviewing a target company's contracts, licences and liabilities before purchase",
    ],
    docs: [
      "Passports or ID cards of promoters, directors and shareholders",
      "Reserved company name, or two or three name options",
      "Registered address with evidence of the right to use the premises",
      "Intended business activities described as they will actually be carried out",
      "Capital structure and the shareholder list",
      "Existing company affidavit, articles and minutes for an existing entity",
    ],
    steps: [
      "Tell the team what the business will actually do and who will hold shares",
      "We confirm activity restrictions and propose a workable structure",
      "We prepare the incorporation or amendment documents for signature",
      "We file with the registry and follow up until the record is updated",
      "We review or draft the first contracts the company needs to operate",
      "We hand over the document set and flag the annual obligations that follow",
    ],
    cautions: [
      {
        title: "Objectives that are too narrow block later steps",
        body: "Registered objectives should cover what the company actually does and what it plans to do soon. Too narrow, and banks, licensing authorities or tender processes ask for amendments first.",
      },
      {
        title: "Nominee shareholding is a legal risk, not a shortcut",
        body: "Holding shares through a Thai nominee to work around foreign-ownership limits creates risk for the company and the shareholders. We do not advise that route and instead look for a structure that is lawful for your activity.",
      },
      {
        title: "Missing minutes surface years later",
        body: "Annual meetings, resolutions and share transfers all need records. Their absence usually becomes visible at the worst moment: financing, a licence application or the sale of the business.",
      },
    ],
    compare: {
      heading: "Ways to do business in Thailand",
      intro:
        "The form you choose affects registration documents, accounting obligations and how easily you can bring investors in later. This table compares the three most common options.",
      columns: ["Sole proprietor", "Registered partnership", "Limited company"],
      rows: [
        {
          label: "Owner's liability",
          cells: [
            "Unlimited, against personal assets",
            "Depends on partner type; both limited and unlimited exist",
            "Shareholders are liable up to the unpaid amount on their shares",
          ],
        },
        {
          label: "Formation documents",
          cells: [
            "Fewest",
            "Partnership agreement and registration application",
            "Affidavit, articles, shareholder register and meeting minutes",
          ],
        },
        {
          label: "Accounting burden",
          cells: ["Lightest", "Financial statements must be prepared and filed", "Audited statements and an annual general meeting each year"],
        },
        {
          label: "Bringing in investors",
          cells: ["Difficult", "Possible, but the partnership agreement must be amended", "Most flexible: share transfers and capital increases"],
        },
        {
          label: "Common pitfall",
          cells: [
            "Mixing personal and business bank accounts",
            "Not defining the managing partner's authority",
            "Registered objectives that do not match the real business, and incomplete minutes",
          ],
        },
      ],
      note: "Foreign investors have additional considerations regarding activity type and shareholding ratios. Check these before filing, not after.",
    },
    handoff:
      "The firm can run the whole set-up: name reservation, incorporation documents, registry filing, the first contract templates the company needs, and coordination with accounting and licensing. That keeps the registry record, the contracts and the books consistent with each other.",
    faqs: [
      {
        question: "What documents are needed to incorporate a Thai company?",
        answer:
          "Generally a reserved name, copies of the promoters' and directors' ID cards or passports, a registered address with evidence of the right to use it, business objectives matching the real activity, and the capital structure with the shareholder list. Foreign shareholders may need additional documents depending on the activity.",
      },
      {
        question: "Should the objectives be drafted broadly or narrowly?",
        answer:
          "They should cover what the company does now and what it plans to do in the near term. Objectives that are too narrow cause problems when opening bank accounts, applying for licences or joining tenders; objectives that are needlessly broad can attract requests for further documents.",
      },
      {
        question: "How much of a Thai company can a foreigner own?",
        answer:
          "It depends on the business activity. Some activities restrict foreign shareholding, some require a licence, and some can be pursued through investment-promotion channels. We check the activity you will actually carry out before proposing a structure.",
      },
      {
        question: "Do contracts have to be in Thai?",
        answer:
          "Bilingual contracts are common and acceptable. State clearly which language version prevails if the texts conflict. Where a contract must be filed with a Thai authority or used in a Thai court, a certified Thai translation is usually required.",
      },
      {
        question: "What does a contract review focus on?",
        answer:
          "Scope of work and payment terms, warranties and liability, limitation of liability, termination events, confidentiality, intellectual-property ownership, and the governing law together with the dispute-resolution route. Those clauses decide what happens when the relationship goes wrong.",
      },
      {
        question: "Is an annual shareholders' meeting really required?",
        answer:
          "A limited company must hold an annual general meeting within the statutory period to approve the financial statements. Not holding it, or not keeping the minutes, regularly becomes an obstacle in later financing, licensing or sale transactions.",
      },
      {
        question: "Can a power of attorney signed abroad be used in Thailand?",
        answer:
          "Yes, once it has gone through the correct certification chain: signature certification in the country of origin, a certified Thai translation, and legalisation through the route the receiving authority requires. The order of those steps matters and is often reversed by mistake.",
      },
      {
        question: "What does legal due diligence look at before a business purchase?",
        answer:
          "Registry records and shareholding, key contracts and transfer restrictions, the licences the business operates under, encumbrances and security, pending disputes, employment obligations, and intellectual-property ownership. The findings usually affect price and warranties in the sale agreement.",
      },
    ],
    related: ["tax", "labour", "ip"],
    imageAlt: "A lawyer explaining commercial contract terms to executives in a law-office meeting room",
  },

  family: {
    slug: "family",
    name: "Family Law",
    tagline: "Marriage, prenuptial agreements, divorce, child custody and support",
    seoTitle: "Family Lawyers in Thailand — Marriage, Divorce & Custody",
    seoDesc:
      "Thai-foreign marriage registration, prenuptial agreements, divorce, custody and child support, legitimation and adoption, with document lists, real steps and pitfalls.",
    answer:
      "Family work covers registering a Thai-foreign marriage and the supporting documents it requires, prenuptial agreements, divorce by mutual consent or by court judgment, custody and child support, legitimation of a child, and adoption. The firm handles Thai civil-registration documents and foreign documents in the same matter, because most delays come from the foreign side of the file.",
    audience: [
      "Thai-foreign couples preparing documents for marriage registration",
      "Couples who want a prenuptial agreement covering property",
      "People seeking a divorce and an agreement on children and property",
      "Parents who need custody or child-support arrangements recorded properly",
      "Anyone dealing with legitimation of a child or an adoption",
    ],
    scope: [
      "Document preparation for Thai-foreign marriage registration",
      "Drafting prenuptial agreements and recording them at registration",
      "Divorce by mutual consent, including the settlement record",
      "Contested divorce proceedings before the court",
      "Custody, visitation and child-support arrangements",
      "Legitimation of a child and adoption procedures",
    ],
    examples: [
      "A foreign fiancé needs an affirmation of freedom to marry translated and legalised",
      "A couple wants property kept separate before marriage",
      "Both spouses agree to divorce and need the agreement drafted properly",
      "One spouse will not agree and the matter must go to court",
      "A father who was never married to the mother wants legal parental rights",
      "A Thai divorce judgment needs to be recognised abroad, or the reverse",
    ],
    docs: [
      "Passports or ID cards of both parties",
      "House registration for the Thai party",
      "Affirmation of freedom to marry, or single-status certificate, for the foreign party",
      "Marriage certificate, for divorce or property matters",
      "Birth certificates of any children",
      "Existing agreements, foreign judgments or registry records relating to status",
    ],
    steps: [
      "Tell the team what outcome you need and in which country it must be valid",
      "We list the documents required on both the Thai and the foreign side",
      "We arrange translation, certification and legalisation where needed",
      "We draft the agreement, petition or registration documents",
      "We attend the district office or the court with you as required",
      "We confirm the registry record and prepare copies for use abroad",
    ],
    cautions: [
      {
        title: "The foreign document chain sets the timetable",
        body: "Single-status affirmations, translations and legalisation each take their own time and differ by nationality. Booking a wedding date before that chain is confirmed is the most common source of last-minute problems.",
      },
      {
        title: "A vague settlement record is hard to enforce",
        body: "Divorce agreements that do not state amounts, payment dates, custody and visitation precisely often cannot be enforced later. Specific wording is what makes an agreement usable if the other side stops complying.",
      },
      {
        title: "A foreign divorce does not update the Thai registry by itself",
        body: "Until the foreign judgment has been translated, certified and recorded through the correct channel, Thai records may still show you as married, which affects later marriage, property and inheritance steps.",
      },
    ],
    compare: {
      heading: "Divorce by mutual consent compared with divorce by court judgment",
      intro:
        "If both parties agree on every issue, registering the divorce at a district office is far simpler. Where children or property remain unresolved, the matter has to go through the court.",
      columns: ["Divorce by mutual consent", "Divorce by court judgment"],
      rows: [
        {
          label: "Starting condition",
          cells: ["Both parties consent and can attend to sign", "One party does not consent, or statutory grounds are relied on"],
        },
        {
          label: "Where it happens",
          cells: ["The district office that registers divorces", "The court with jurisdiction"],
        },
        {
          label: "Main documents",
          cells: [
            "Marriage certificate, ID or passport, house registration and the divorce settlement record",
            "The same, plus evidence supporting the grounds and documents on children and property",
          ],
        },
        {
          label: "Children",
          cells: [
            "Custody, visitation and support must be stated clearly in the agreement",
            "The court decides with the child's best interests as the guiding consideration",
          ],
        },
        {
          label: "Watch out for",
          cells: [
            "Wording that is too general to enforce later",
            "Evidence that does not match the grounds relied on, and several hearing dates",
          ],
        },
      ],
      note: "Where one spouse is foreign, check in advance what form of document their home country needs so the divorce is also effective there.",
    },
    handoff:
      "The firm can handle the whole file: the Thai registry documents, the translations and legalisation of the foreign documents, the drafting, and attendance at the district office or the court. Family matters are treated as confidential lawyer-client work throughout.",
    faqs: [
      {
        question: "What does a foreigner need to marry in Thailand?",
        answer:
          "Usually an affirmation of freedom to marry issued by their embassy or home authority, translated into Thai with the translation certified and then legalised, plus a valid passport. The Thai party uses their ID card and house registration. The exact requirements vary by nationality, so confirm before booking a date.",
      },
      {
        question: "Are prenuptial agreements valid in Thailand?",
        answer:
          "Yes, but the agreement must be made before the marriage is registered and recorded together with the marriage registration as the law requires. Its terms must not conflict with law or public order, so it is worth having it drafted and reviewed well before the registration date.",
      },
      {
        question: "Who owns property acquired before the marriage?",
        answer:
          "Property owned before the marriage is in principle personal property, while property acquired during the marriage is generally marital property. The common problem is mixing personal funds into marital assets until they cannot be traced, so keep evidence of how each asset was acquired.",
      },
      {
        question: "Who will the children live with after a divorce?",
        answer:
          "If the parents agree, the settlement record should state who exercises parental power, how visitation works and what support is paid. If they cannot agree, the court decides with the child's best interests as the guiding consideration rather than the parents' preferences.",
      },
      {
        question: "How is child support determined?",
        answer:
          "It is assessed against the child's needs and the paying parent's ability to pay. The agreement should state the amount, the payment date, the payment method and how it may be adjusted in future, so that it can be enforced if payments stop.",
      },
      {
        question: "Does an unmarried father have rights over his child?",
        answer:
          "Legitimation is needed first before full legal rights and duties arise. It can be done through the registry where the required consents exist, or through a court judgment where there is a dispute about paternity or consent.",
      },
      {
        question: "Can a Thai marriage certificate be used abroad?",
        answer:
          "Generally it must be translated and certified in the form the receiving country requires. Some countries accept documents legalised through the Ministry of Foreign Affairs, others accept a form under a convention they are party to. Check with the receiving authority before starting.",
      },
      {
        question: "I divorced abroad. What do I need to do in Thailand?",
        answer:
          "The foreign judgment or divorce record has to be translated and certified through the correct channel, then used to bring your Thai registry status into line. Without that step Thai records may still show the marriage as subsisting, which causes problems later.",
      },
    ],
    related: ["estate", "notary-international", "property"],
    imageAlt: "A lawyer advising a client on family and civil-registration documents at a meeting table",
  },

  estate: {
    slug: "estate",
    name: "Estates, Wills & Succession",
    tagline: "Wills, estate administrators and transferring assets to heirs",
    seoTitle: "Estate & Probate Lawyers in Thailand — Wills and Administrators",
    seoDesc:
      "Drafting Thai wills, petitioning for an estate administrator, transferring land, bank accounts and shares to heirs, with documents, real steps and pitfalls.",
    answer:
      "Estate work covers drafting a will in a valid form, petitioning the court to appoint an estate administrator, collecting and transferring estate assets such as land, bank accounts and shares, and managing disagreements between heirs. The firm checks civil-registration records and asset documents before setting the order of steps, because banks and land offices each ask for different proof.",
    audience: [
      "Heirs whose bank or land office is asking for a court order appointing an administrator",
      "People who want a will prepared so their family does not face a dispute",
      "Families with several asset types across several provinces",
      "Heirs living abroad who need to act through a representative",
      "Families where the heirs disagree about how assets should be divided",
    ],
    scope: [
      "Drafting wills in a form that meets Thai requirements",
      "Petitioning the court for appointment of an estate administrator",
      "Collecting estate assets and dealing with banks and the land office",
      "Transferring land, deposits, vehicles and shares to the heirs",
      "Advising on heir classes and shares where there is no will",
      "Representing heirs where the division is contested",
    ],
    examples: [
      "A bank will not release a deceased parent's account without a court order",
      "Land held in the deceased's name must be transferred to several heirs",
      "A will exists but its form or witnesses are being questioned",
      "An heir abroad cannot attend and needs to appoint a representative",
      "Company shares form part of the estate and must be transferred",
      "Heirs disagree about who should be appointed administrator",
    ],
    docs: [
      "Death certificate of the deceased",
      "House registration and ID card of the deceased and of the heirs",
      "Marriage or divorce records and birth certificates showing the family line",
      "The original will, if one exists",
      "Title deeds, bank passbooks, share certificates and vehicle registrations",
      "Consent letters from heirs who are not the petitioner, where available",
    ],
    steps: [
      "Bring the death certificate, family records and a list of known assets",
      "We map the heirs and confirm which assets need a court order",
      "We prepare the petition and gather the heirs' consents",
      "We file with the court with jurisdiction and attend the hearing",
      "Once the order is issued, we deal with banks and the land office",
      "We complete the transfers and hand over the closing documents",
    ],
    cautions: [
      {
        title: "A will in the wrong form can fail entirely",
        body: "Thai law recognises several forms of will, each with its own requirements for witnesses and signatures. A will that does not meet the requirements of the form it purports to use is open to challenge exactly when it matters.",
      },
      {
        title: "Family records decide who counts as an heir",
        body: "Marriage records, legitimation and adoption all change the heir list. Where those records are incomplete or inconsistent, the petition stalls until the civil-registration documents are corrected or obtained.",
      },
      {
        title: "Each asset holder has its own requirements",
        body: "Banks, the land office and company registrars each ask for a different combination of the court order, identification and consents. Collecting them in one pass is faster than discovering the gaps one institution at a time.",
      },
    ],
    compare: {
      heading: "With a will compared with no will",
      intro:
        "A will does not remove the need for an administrator in most cases, but it changes how the estate is divided and how much room there is for dispute.",
      columns: ["Valid will exists", "No will"],
      rows: [
        {
          label: "How assets are divided",
          cells: ["According to the will, within the limits of law", "By statutory heir classes and shares"],
        },
        {
          label: "Administrator",
          cells: ["Usually the person named, subject to the court's appointment", "The court appoints from among the eligible applicants"],
        },
        {
          label: "Room for dispute",
          cells: ["Lower where the form and wording are sound", "Higher, particularly where family records are incomplete"],
        },
        {
          label: "Documents needed",
          cells: ["Will, death certificate, family records, asset documents", "Death certificate, complete family records, asset documents, heir consents"],
        },
        {
          label: "Common pitfall",
          cells: [
            "A will that omits assets acquired later, or names an unwilling executor",
            "Assumptions about who inherits that do not match the statutory classes",
          ],
        },
      ],
      note: "Reviewing a will every few years, and after any major asset or family change, prevents most of the disputes we see.",
    },
    handoff:
      "The firm can handle the estate from the first document check through to the final transfer: the petition, the hearing, the bank and land-office steps, and the certified translations or powers of attorney needed for heirs abroad.",
    faqs: [
      {
        question: "Why does the bank ask for a court order?",
        answer:
          "Banks generally release a deceased account holder's funds only to a court-appointed estate administrator, because that order identifies who is legally entitled to act for the estate. The same applies to most land-office transfers, so the petition is usually the first practical step.",
      },
      {
        question: "How long does appointing an administrator take?",
        answer:
          "It depends on the court's schedule, whether the family records are complete, and whether any heir objects. Missing or inconsistent civil-registration documents are the most common reason a petition takes longer than expected, so those are checked first.",
      },
      {
        question: "Can an heir who lives abroad take part?",
        answer:
          "Yes. Consents and powers of attorney signed abroad can normally be used once they have been certified and, where required, translated and legalised. Some steps still need the appointed administrator to appear in person in Thailand.",
      },
      {
        question: "Does a foreigner's will cover assets in Thailand?",
        answer:
          "A foreign will can be relevant, but using it for Thai assets usually requires translation, certification and a Thai court process, and its terms must work with Thai rules on land and heirship. Many people therefore also prepare a Thai will for their Thai assets.",
      },
      {
        question: "What if the heirs cannot agree?",
        answer:
          "The matter becomes contested and the court decides on the appointment and, where necessary, on the division. Bringing the documents in early usually widens the options for a recorded agreement before the dispute hardens.",
      },
      {
        question: "Can a will be changed later?",
        answer:
          "Yes. A will can be revoked or replaced while the testator has capacity. Problems arise when several versions exist without clear dates or without revoking the earlier ones, so keep the record of which document is current.",
      },
      {
        question: "Are debts of the deceased inherited?",
        answer:
          "Estate liabilities are settled from the estate. Heirs are not personally liable beyond what they receive, but the administrator has to account for debts before distribution, which is why the asset and liability list matters at the start.",
      },
      {
        question: "Should a will name the assets individually?",
        answer:
          "Naming key assets helps, but a will that lists only current assets can leave later acquisitions unaddressed. A combination of specific gifts and a clear residuary provision is usually more durable, and it should be reviewed after major changes.",
      },
    ],
    related: ["family", "property", "notary-international"],
    imageAlt: "A lawyer reviewing title deeds and inheritance documents with a family at an office table",
  },

  property: {
    slug: "property",
    name: "Property & Real Estate",
    tagline: "Title checks, sale and purchase, leases and condominium transfers",
    seoTitle: "Property Lawyers in Thailand — Title Checks & Transfers",
    seoDesc:
      "Land and condominium due diligence, sale and purchase agreements, leases, land-office transfers and foreign-ownership rules, with documents, steps and pitfalls.",
    answer:
      "Property work covers checking title and encumbrances at the land office, drafting or reviewing sale, purchase and lease agreements, preparing the transfer documents, and attending the land office on the transfer day. For foreign buyers we confirm what form of ownership or lease is actually available for the property in question before any deposit is paid.",
    audience: [
      "Buyers who want title and encumbrances checked before paying a deposit",
      "Foreign buyers of a condominium unit or a long lease",
      "Sellers who need the transfer documents and taxes prepared correctly",
      "Landlords and tenants entering a long-term lease",
      "Owners in a dispute over boundaries, access or a developer's obligations",
    ],
    scope: [
      "Title and encumbrance searches at the land office",
      "Reviewing developer contracts, reservation forms and payment schedules",
      "Drafting sale, purchase and lease agreements",
      "Advising on foreign ownership, leasehold and condominium rules",
      "Preparing transfer documents and attending the land office",
      "Disputes over boundaries, servitudes, access and construction defects",
    ],
    examples: [
      "A buyer wants the title deed verified before signing a reservation form",
      "A condominium purchase requires the foreign-currency and quota conditions checked",
      "A long lease needs drafting and registering at the land office",
      "A developer is late and the buyer wants to know the contractual position",
      "A seller needs the transfer taxes and fees calculated before agreeing a price",
      "A neighbour blocks the access route to a plot",
    ],
    docs: [
      "Title deed or condominium unit title, front and back",
      "ID card or passport of buyer and seller, plus house registration",
      "Marriage or divorce records where relevant to consent",
      "The reservation form, developer contract or draft sale agreement",
      "Company affidavit and minutes where a company is a party",
      "Evidence of funds and, for foreign condominium buyers, the transfer records",
    ],
    steps: [
      "Send the title deed and draft contract for review before you pay a deposit",
      "We search the title and encumbrances at the land office",
      "We report on what the title actually allows and what the contract commits you to",
      "We draft or amend the agreement and the transfer documents",
      "We calculate the fees and taxes payable on transfer",
      "We attend the land office and confirm the record after registration",
    ],
    cautions: [
      {
        title: "The deed and the contract must agree",
        body: "Names, plot numbers, area and encumbrances on the deed regularly differ from the contract. The land office follows the deed and its records, so differences are resolved before transfer day, not on it.",
      },
      {
        title: "Foreign ownership rules are property-specific",
        body: "What is available depends on the type of property and the route used. Structures built to work around the rules create risk for the buyer, and we advise on what is actually possible instead.",
      },
      {
        title: "Deposits paid before due diligence are hard to recover",
        body: "Reservation forms often make the deposit non-refundable. Reviewing the title and the contract before payment costs far less than trying to unwind the transaction afterwards.",
      },
    ],
    compare: {
      heading: "Condominium ownership, long lease and company holding",
      intro:
        "Foreign buyers usually compare these three routes. They differ in what you actually hold, what documents exist, and what happens at the end.",
      columns: ["Condominium unit ownership", "Registered long lease", "Holding through a company"],
      rows: [
        {
          label: "What you hold",
          cells: ["Unit title in your own name, subject to statutory conditions", "Contractual right to use for the registered term", "Shares in a company that owns the property"],
        },
        {
          label: "Registration",
          cells: ["Registered at the land office in the buyer's name", "Lease registered on the title for the agreed term", "Property registered to the company, not to you"],
        },
        {
          label: "Key documents",
          cells: ["Unit title, transfer records, condominium documents", "Lease agreement and land-office registration", "Company documents, minutes, and the property title"],
        },
        {
          label: "Ongoing obligations",
          cells: ["Common-area fees and condominium rules", "Rent and the lease terms", "Company accounting, filings and annual meetings"],
        },
        {
          label: "Main risk to check",
          cells: [
            "Conditions attached to foreign purchase, including transfer evidence",
            "Terms on renewal, transfer and what happens on the owner's death",
            "Structures created only to hold land can be challenged; take advice first",
          ],
        },
      ],
      note: "Which route is available depends on the specific property and on the buyer's circumstances. Confirm this before paying anything.",
    },
    handoff:
      "The firm can handle the transaction end to end: the title search, the contract, the tax calculation, the transfer documents and attendance at the land office, plus certified translations or a power of attorney where the buyer or seller cannot attend.",
    faqs: [
      {
        question: "What does a title check actually reveal?",
        answer:
          "It confirms the registered owner, the plot or unit details and the area, and shows registered encumbrances such as mortgages, servitudes and usufructs, plus restrictions noted on the record. That is the information the land office will rely on at transfer, so it is checked before a deposit is paid.",
      },
      {
        question: "Can a foreigner own land in Thailand?",
        answer:
          "Land ownership by foreigners is restricted. Condominium units can be owned in a foreign buyer's name subject to statutory conditions, and long leases are registrable. Structures created solely to work around the land rules carry legal risk, so take advice on what is available for your specific property.",
      },
      {
        question: "Who pays the transfer fees and taxes?",
        answer:
          "That is a matter of agreement between the parties and should be written into the contract, because several items are involved and they are calculated on assessed or contract value, holding period and the seller's status. Calculate them before agreeing a price rather than at the land office.",
      },
      {
        question: "How long can a lease be registered for?",
        answer:
          "Thai law caps the registrable term for a lease of immovable property, and anything beyond that term is not registrable as a single lease. Renewal promises are contractual and depend on their wording, so they need careful drafting rather than reliance on a marketing brochure.",
      },
      {
        question: "The developer is late. What are my options?",
        answer:
          "Start with the contract: the delivery date, the delay provisions and the termination clause decide what you can claim. Keep every payment record and written exchange, because a claim or negotiation will rest on those documents.",
      },
      {
        question: "Do I have to attend the land office in person?",
        answer:
          "Not necessarily. A properly drafted power of attorney can allow a representative to act, and one signed abroad usually needs certification and a certified Thai translation. Some steps still require the parties themselves, so confirm before booking travel.",
      },
      {
        question: "What should be checked before buying off-plan?",
        answer:
          "The seller's title to the land, the project approvals, the payment schedule against construction progress, the specification and area tolerance, the delay and termination clauses, and what security exists for your payments. Reservation forms are usually drafted for the developer, not the buyer.",
      },
      {
        question: "Is a spouse's consent needed to sell?",
        answer:
          "Where the property is marital property, the transaction generally requires the spouse's consent, and the land office will look for it. Marriage and divorce records therefore need to be consistent with the deed before the transfer date.",
      },
    ],
    related: ["estate", "corporate", "litigation"],
    imageAlt: "A property lawyer examining a Thai land title deed and a survey plan on a desk",
  },
};
